import React from 'react';
import { Briefcase, Award, Download, CheckCircle } from 'lucide-react';

const Resume = () => {
  const experiences = [
    {
      company: 'Grupo Fleury',
      role: 'Operador Telemarketing',
      period: 'Maio/2025 - Presente',
      description: 'Responsável por agendamento de exames de Imagem e laboratoriais para o Grupo Hermes Pardini, representando a Empresa Centro de Medicina em todas as unidades do Rio de Janeiro e Baixada. Atua via WhatsApp e telefone.',
    },
    {
      company: 'PONTOCOM',
      role: 'Consultor de Vendas',
      period: 'Fevereiro/2022 - Janeiro/2024',
      description: 'Realiza vendas consultivas de produtos financeiros e proteção veicular. Responsável por contato com clientes ativos e inativos para prospecção e fidelização. Desenvolve propostas comerciais e conduz negociações até o fechamento. Opera multicanal via WhatsApp, e-mail e telefone. Gerencia CRM e acompanha todo o funil de vendas.',
    },
    {
      company: 'Iate Clube do Rio de Janeiro',
      role: 'Analista Fiscal',
      period: 'Outubro/2012 - Janeiro/2019',
      description: 'Responsável pelo controle fiscal e retenções de impostos (IRRF, PIS, COFINS, CSLL, INSS, ISS). Interface com contabilidade para fechamento de balanço. Elaboração de relatórios gerenciais e apoio à diretoria.',
    },
    {
      company: 'Meta Asset Management Ltda',
      role: 'Assistente Administrativo (Back Office)',
      period: 'Julho/2010 - Setembro/2012',
      description: 'Liquidação de operações financeiras na BM&F/Bovespa. Executa conciliação bancária, contas a pagar e relatórios de risco de carteiras (VAR). Emite relatórios gerenciais e libera cotas de fundos de investimento.',
    },
    {
      company: 'Restaurante Gula Gula',
      role: 'Assistente Administrativo Financeiro',
      period: 'Março/2008 - Junho/2008',
      description: 'Gestão de contas a pagar e receber, conciliação bancária e relatórios financeiros.',
    },
    {
      company: 'Amil Assistência Médica',
      role: 'Representante Comercial',
      period: 'Fevereiro/2006 - Março/2007',
      description: 'Comercializa planos de saúde e odontológicos. Supera metas com desempenho acima de 80%.',
    },
  ];

  const skills = [
    'Controle Fiscal e Retenções Tributárias',
    'Contas a Pagar e Conciliação Bancária',
    'Relatórios Gerenciais',
    'Apoio à Diretoria',
    'Organização e Gestão Administrativa',
    'Suíte Office Avançado',
    'Sistemas de Gestão',
    'Internet e Ferramentas Digitais',
    'CRM e Funil de Vendas',
    'Atendimento Multicanal',
  ];

  return (
    <div data-testid="resume-page" className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-20 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-4">Currículo Profissional</h1>
            <div className="w-24 h-1 bg-yellow-400 mx-auto mb-8"></div>
            <a
              href="https://customer-assets.emergentagent.com/job_meslin-dev/artifacts/abxxh8qo_CV%20Fernando%202026%20%285%29%20%281%29.pdf"
              download="Curriculo_Fernando_Meslin.pdf"
              data-testid="download-resume-button"
              className="inline-flex items-center justify-center px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold rounded-full transition-all duration-300 hover:scale-105 shadow-xl"
            >
              <Download className="mr-2" size={20} />
              Baixar Currículo em PDF
            </a>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <Briefcase className="text-blue-900" size={24} />
              </div>
              <h2 className="text-4xl font-bold text-blue-900">Experiência Profissional</h2>
            </div>

            <div className="space-y-8">
              {experiences.map((exp, index) => (
                <div
                  key={index}
                  data-testid={`experience-${index}`}
                  className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-blue-900 mb-2">{exp.role}</h3>
                      <p className="text-xl text-gray-700 font-semibold">{exp.company}</p>
                    </div>
                    <span className="inline-block mt-2 md:mt-0 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                      {exp.period}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{exp.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Skills Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                <Award className="text-yellow-600" size={24} />
              </div>
              <h2 className="text-4xl font-bold text-blue-900">Competências</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {skills.map((skill, index) => (
                <div key={index} data-testid={`skill-${index}`} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
                  <span className="text-gray-700">{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Certifications Section */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-2xl shadow-xl p-8">
            <h2 className="text-3xl font-bold mb-6">Certificações</h2>
            <div data-testid="certification" className="flex items-start space-x-4">
              <Award className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-xl font-semibold mb-2">Curso de Retenções de Impostos</h3>
                <p className="text-gray-200">Cenofisco - 2012</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resume;